import { Link } from "react-router-dom";
import { ArrowLeft, ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useLanguage } from "@/contexts/LanguageContext";
import { usePublicHowItWorksContent, usePublicFAQs } from "@/hooks/api/use-public";
import { Skeleton } from "@/components/ui/skeleton";
import { DynamicIcon } from "@/components/ui/DynamicIcon";

const HowItWorks = () => {
  const { t, isRTL, language } = useLanguage();
  const ArrowIcon = isRTL ? ArrowLeft : ArrowRight;
  const { data: howItWorksContent, isLoading: isLoadingContent } = usePublicHowItWorksContent();
  const { data: faqsData, isLoading: isLoadingFAQs } = usePublicFAQs();

  const content = howItWorksContent?.data?.content;

  const heroTitle = content?.hero_title
    ? (typeof content.hero_title === 'string'
        ? content.hero_title
        : content.hero_title[language] || content.hero_title.en)
    : null;

  const heroSubtitle = content?.hero_subtitle
    ? (typeof content.hero_subtitle === 'string'
        ? content.hero_subtitle
        : content.hero_subtitle[language] || content.hero_subtitle.en)
    : null;

  const ctaButtonText = content?.cta_button_text
    ? (typeof content.cta_button_text === 'string'
        ? content.cta_button_text
        : content.cta_button_text[language] || content.cta_button_text.en)
    : null;

  const ctaButtonUrl = content?.cta_button_url || '/auth?mode=register';

  const steps = howItWorksContent?.data?.steps?.filter(s => s.is_active).sort((a, b) => a.step_number - b.step_number) || [];
  const faqs = faqsData?.data?.filter(f => f.is_active) || [];

  return (
    <div className="py-16">
      {/* Hero */}
      <section className="py-20 bg-muted">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto text-center">
            {isLoadingContent || !heroTitle ? (
              <>
                <Skeleton className="h-12 w-full max-w-2xl mx-auto mb-6" />
                <Skeleton className="h-6 w-full max-w-xl mx-auto" />
              </>
            ) : (
              <>
                <h1 className="text-4xl md:text-5xl font-bold text-foreground mb-6 animate-fade-up">
                  {heroTitle}
                </h1>
                <p className="text-lg text-muted-foreground animate-fade-up stagger-1">
                  {heroSubtitle}
                </p>
              </>
            )}
          </div>
        </div>
      </section>

      {/* Steps */}
      <section className="py-20">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            {isLoadingContent || steps.length === 0 ? (
              <div className="space-y-12">
                {[1, 2, 3, 4].map((i) => (
                  <Skeleton key={i} className="h-32" />
                ))}
              </div>
            ) : (
              steps.map((step, index) => {
                const title = typeof step.title === 'string'
                  ? step.title
                  : (step.title?.[language] || step.title?.en || '');
                const description = typeof step.description === 'string'
                  ? step.description
                  : (step.description?.[language] || step.description?.en || '');

                return (
                  <div
                    key={step.id}
                    className="flex gap-6 mb-12 last:mb-0"
                  >
                    <div className="flex flex-col items-center">
                      <div className="w-20 h-20 bg-primary/10 rounded-full flex items-center justify-center text-primary">
                        {step.icon ? (
                          <DynamicIcon 
                            iconName={step.icon} 
                            className="w-10 h-10 text-primary" 
                            size={40}
                            fallback={<span className="text-2xl">📋</span>}
                          />
                        ) : (
                          <span className="text-2xl">📋</span>
                        )}
                      </div>
                      {index < steps.length - 1 && (
                        <div className="w-0.5 h-full bg-primary/20 mt-4" />
                      )}
                    </div>
                    <div className="flex-1 pt-4">
                      <h3 className="text-2xl font-bold text-foreground mb-3">
                        {step.step_number}. {title}
                      </h3>
                      <p className="text-muted-foreground text-lg">
                        {description}
                      </p>
                    </div>
                  </div>
                );
              })
            )}
          </div>

          <div className="text-center mt-16">
            {ctaButtonText && (
              <Button size="lg" asChild>
                <Link to={ctaButtonUrl}>
                  {ctaButtonText}
                  <ArrowIcon size={18} />
                </Link>
              </Button>
            )}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-20 bg-muted">
        <div className="container mx-auto px-4">
          <h2 className="text-3xl font-bold text-foreground text-center mb-12">
            {t('howitworks.faq')}
          </h2>
          {isLoadingFAQs || faqs.length === 0 ? (
            <div className="max-w-3xl mx-auto space-y-6">
              {[1, 2, 3].map((i) => (
                <Skeleton key={i} className="h-32 rounded-xl" />
              ))}
            </div>
          ) : (
            <div className="max-w-3xl mx-auto space-y-6">
              {faqs.map((faq) => {
                const question = typeof faq.question === 'string'
                  ? faq.question
                  : (faq.question?.[language] || faq.question?.en || '');
                const answer = typeof faq.answer === 'string'
                  ? faq.answer
                  : (faq.answer?.[language] || faq.answer?.en || '');

                return (
                  <div key={faq.id} className="bg-card p-6 rounded-xl shadow-md">
                    <h3 className="text-lg font-bold text-foreground mb-2">
                      {question}
                    </h3>
                    <p className="text-muted-foreground">
                      {answer}
                    </p>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </section>
    </div>
  );
};

export default HowItWorks;
